import request from '../request';
import { schoolAPI, invoiceManagementAPI } from './urls';

// The duplicate-check endpoints return a BaseModel wrapper with a PascalCase data body.
// Fold it to a predictable camelCase shape, tolerating either casing on every key.
export const normaliseDuplicate = (response) => {
  const data = response?.data ?? response ?? {};
  return {
    isDuplicate: data.isDuplicate ?? data.IsDuplicate ?? false,
    matchedBy: data.matchedBy ?? data.MatchedBy ?? null,
    existingId: data.existingId ?? data.ExistingId ?? null,
    existingName: data.existingName ?? data.ExistingName ?? null,
  };
};

const getSchoolList = (schoolsWithLiveSurveys) => {
  return request({
    url: schoolAPI.getAllSchools(schoolsWithLiveSurveys),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchoolListPaginated = (pageNumber = 1, pageSize = 20, sortBy = 'Id', sortDirection = 'asc', searchTerm = '', includeOnlyTestAccounts = false) => {
  const params = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
    sortBy,
    sortDirection,
  });
  
  if (searchTerm) {
    params.append('searchTerm', searchTerm);
  }
  
  if (includeOnlyTestAccounts) {
    params.append('includeOnlyTestAccounts', 'true');
  }
  
  return request({
    url: `${schoolAPI.getAllSchoolsPaginated}?${params.toString()}`,
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const addSchool = (school) => {
  return request({
    url: schoolAPI.api,
    method: 'POST',
    data: school,
  })
    .then((response) => response)
    .catch((error) => error);
};

const addNewIndividualSchoolPost = (school) => {
  //debugger;
  return request({
    url: schoolAPI.addNewIndividualSchoolPost,
    method: 'POST',
    data: school,
  })
    .then((response) => response)
    .catch((error) => error);
};

// The API serialises the import result PascalCase inside the lowercase BaseModel wrapper;
// normalise every field the UI reads to one stable camelCase shape (same o.value ?? o.Value
// tolerance the duplicate check uses) so a serialisation change can only break in one place.
const normaliseBulkRowFields = (fields) =>
  !fields ? null : {
    name: fields.Name ?? fields.name,
    urn: fields.Urn ?? fields.urn,
    numberOfPupil: fields.NumberOfPupil ?? fields.numberOfPupil,
    address: fields.Address ?? fields.address,
    postcode: fields.Postcode ?? fields.postcode,
    country: fields.Country ?? fields.country,
    schoolType: fields.SchoolType ?? fields.schoolType,
    isBoarding: fields.IsBoarding ?? fields.isBoarding,
    isPost16: fields.IsPost16 ?? fields.isPost16,
    hasNursery: fields.HasNursery ?? fields.hasNursery,
    religousCharacter: fields.ReligousCharacter ?? fields.religousCharacter,
    diocese: fields.Diocese ?? fields.diocese,
    fsmPercent: fields.FSMPercent ?? fields.fsmPercent,
    isFederated: fields.IsFederated ?? fields.isFederated,
  };

const normaliseBulkRow = (row) => ({
  rowNumber: row?.RowNumber ?? row?.rowNumber,
  name: row?.Name ?? row?.name,
  urn: row?.Urn ?? row?.urn,
  status: row?.Status ?? row?.status,
  errors: row?.Errors ?? row?.errors ?? [],
  schoolId: row?.SchoolId ?? row?.schoolId,
  fields: normaliseBulkRowFields(row?.Fields ?? row?.fields),
});

const normaliseBulkImportResult = (data) => {
  if (!data) return null;
  const rows = data.Rows ?? data.rows;
  if (!Array.isArray(rows)) return null;
  return {
    totalRows: data.TotalRows ?? data.totalRows ?? rows.length,
    addedCount: data.AddedCount ?? data.addedCount ?? 0,
    updatedCount: data.UpdatedCount ?? data.updatedCount ?? 0,
    duplicateCount: data.DuplicateCount ?? data.duplicateCount ?? 0,
    invalidCount: data.InvalidCount ?? data.invalidCount ?? 0,
    failedCount: data.FailedCount ?? data.failedCount ?? 0,
    noMatchCount: data.NoMatchCount ?? data.noMatchCount ?? 0,
    dryRun: data.DryRun ?? data.dryRun ?? false,
    rows: rows.map(normaliseBulkRow),
  };
};

// Bulk school upload. `file` is the selected .xlsx File; posted as multipart form-data
// under the `FormFile` field expected by the BulkAdd endpoint. The optional second
// argument links the upload to a group (matGroupId), selects Add vs Update mode, and
// requests a non-persisting preview (dryRun). Resolves to { success, message, result }
// with `result` normalised for the UI (null when the body carried no row list); rejects
// with the parsed API error body, whose `message` is the server's real reason — callers
// must surface it instead of a generic toast.
const bulkAddSchools = (file, { matGroupId, mode, dryRun } = {}) => {
  const formData = new FormData();
  formData.append('FormFile', file);
  if (matGroupId !== undefined && matGroupId !== null && matGroupId !== '') {
    formData.append('MatGroupId', matGroupId);
  }
  if (mode !== undefined && mode !== null && mode !== '') {
    formData.append('Mode', mode);
  }
  if (dryRun !== undefined && dryRun !== null) {
    formData.append('DryRun', dryRun);
  }
  return request({
    url: schoolAPI.bulkAdd,
    method: 'POST',
    data: formData,
    // Let axios/the browser set multipart/form-data with the correct boundary;
    // the default JSON Content-Type would otherwise break the upload server-side.
    headers: { 'Content-Type': undefined },
  }).then((body) => ({
    success: body?.success ?? false,
    message: body?.message || '',
    result: normaliseBulkImportResult(body?.data),
  }));
};

// Inline duplicate check for a school by name and/or URN. The server answers PascalCase
// inside a BaseModel wrapper; normalise to a stable camelCase shape the callers can rely
// on (same o.value ?? o.Value tolerance the storage-location lookup uses).
const checkSchoolDuplicate = (name, urn, excludeId) => {
  return request({
    url: schoolAPI.checkDuplicate(name, urn, excludeId),
    method: 'GET',
  })
    .then((response) => normaliseDuplicate(response))
    .catch((error) => error);
};

const addNewIndividualSchool = (EstablishmentName,Address3,URN,NumberOfPupils,SchoolProducts) => { 
  //debugger;
  return request({
    url: schoolAPI.addNewIndividualSchool(EstablishmentName,Address3,URN,NumberOfPupils,SchoolProducts),
    method: 'GET', 
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchoolDetail = (id, schoolToken = null) => {
  schoolToken = schoolToken === null ? '' : schoolToken;
  return request({
    url: schoolAPI.getSchoolDetail(id, schoolToken),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchool = (schoolToken = null) => {
  schoolToken = schoolToken === null ? '' : schoolToken;
  return request({
    url: schoolAPI.getSchoolDetailByToken(schoolToken),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getInfo = (id) => {
  return request({
    url: schoolAPI.getInfo(id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchoolDetailBySuperAdmin = (id) => {
  return request({
    url: schoolAPI.getSchoolDetailBySuperAdmin(id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchoolDetailByRegistrationToken = (token) => {
  return request({
    url: schoolAPI.getSchoolDetailByRegistrationToken(token),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const updateSchoolDetail = (school) => {
  return request({
    url: schoolAPI.updateSchoolDetail,
    method: 'PUT',
    data: school,
  })
    .then((response) => response)
    .catch((error) => error);
};

const updateSchoolDetailOnly = (school) => {
  return request({
    url: schoolAPI.UpdateSchoolOnly,
    method: 'PUT',
    data: school,
  })
    .then((response) => response)
    .catch((error) => error);
};
const updateSchoolDetailByHeadTeacher = (school) => {
  return request({
    url: schoolAPI.updateSchoolDetailByHeadTeacher,
    method: 'PUT',
    data: school,
  })
    .then((response) => response)
    .catch((error) => error);
};

const updateSchoolAdminInfoByHeadTeacher = (school) => {
  return request({
    url: schoolAPI.updateSchoolAdminInfoByHeadTeacher,
    method: 'PUT',
    data: school,
  })
    .then((response) => response)
    .catch((error) => error);
};

const archiveSchool = (schoolId) => {
  return request({
    url: schoolAPI.archiveSchool(schoolId),
    method: 'DELETE',
  })
    .then((response) => response)
    .catch((error) => error);
};

// Restores an archived school to the trust's active roster. The POST sends no body, so the
// backend does not create a subscription — that is set up separately afterwards. Kept as a
// distinct, confirmed action from archiving.
const unarchiveSchool = (schoolId) => {
  return request({
    url: schoolAPI.unarchiveSchool(schoolId),
    method: 'POST',
  })
    .then((response) => response)
    .catch((error) => error);
};

const updateSchoolInvoiceInfo = (data) => {
  return request({
    url: schoolAPI.updateSchoolInvoiceInfo,
    method: 'POST',
    data: data,
  })
    .then((response) => response)
    .catch((error) => error);
};
const sendUpgradeInvoice = (data) => {
  return request({
    url: schoolAPI.SendUpgradeInvoice,
    method: 'POST',
    data: data,
  })
    .then((response) => response)
    .catch((error) => error);
};
const updateSchoolWelbeeVoice = (data) => {
  return request({
    url: schoolAPI.updateSchoolWelbeeVoice,
    method: 'PUT',
    data: data,
  })
    .then((response) => response)
    .catch((error) => error);
};

const getAddressByPostCode = (postCode) => {
  return fetch('http://api.postcodes.io/postcodes/' + postCode)
    .then((response) => response.json())
    .then((response) => response)
    .catch((error) => error);
};
const getAddressSuggestions = (keyword) => {
  return request({
    url: schoolAPI.getAddressSuggestions(keyword),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchoolStaff = (schoolId, schoolToken = null) => {
  schoolToken = schoolToken === null ? '' : schoolToken;
  return request({
    url: schoolAPI.getSchoolStaff(schoolId, schoolToken),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const deleteSchoolStaff = (id) => {
  return request({
    url: schoolAPI.deleteSchoolStaff(id),
    method: 'DELETE',
  })
    .then((response) => response)
    .catch((error) => error);
};

const updateSchoolStaff = (schoolStaff) => {
  return request({
    url: schoolAPI.updateSchoolStaff,
    method: 'PUT',
    data: schoolStaff,
  })
    .then((response) => response)
    .catch((error) => error);
};

const hideUpgradeBanner = (schoolId) => {
  return request({
    url: schoolAPI.hideUpgradeBanner(schoolId),
    method: 'PUT',
  })
    .then((response) => response)
    .catch((error) => error);
};

const getNumberOfPupil = (schoolId, token) => {
  return request({
    url: schoolAPI.getNumberOfPupil(schoolId === null ? 0 : schoolId, token),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const GetSchoolsForMat = () => {
  return request({
    url: schoolAPI.GetSchoolsForMat,
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const GetSchoolsForMatWithCloseSurvey = () => {
  return request({
    url: schoolAPI.GetSchoolsForMatWithCloseSurvey,
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};


const GetGIASSchools = () => {
  return request({
    url: schoolAPI.GetGIASSchools,
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const GetSchoolForMATGUID = (matGUID) => {
  return request({
    url: schoolAPI.GetSchoolForMATGUID(matGUID),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const GetGIASSchoolsByURN = (URN) => {
  return request({
    url: schoolAPI.GetGIASSchoolsByURN(URN),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

const GetMatGroupDetailsFromGIAS = (matGUId) => {
  return request({
    url: schoolAPI.GetMatGroupDetailsFromGIAS(matGUId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

// User Story 1.3: Generate invoice for existing subscription (school or MAT)
const generateInvoiceForSubscription = (requestData) => {
  return request({
    url: invoiceManagementAPI.GenerateInvoiceForSubscription(),
    method: 'POST',
    data: requestData,
  })
    .then((response) => response)
    .catch((error) => error);
};

const getSchoolSubscriptions = (id) => {
  return request({
    url: schoolAPI.getSchoolSubscriptions(id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const addSchoolSubscriptions = (schoolId, id) => {
  return request({
    url: schoolAPI.addSchoolSubscriptions(schoolId, id),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const GetOutstandingInvoices = () => {
  return request({
    url: schoolAPI.GetOutstandingInvoices(),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const SendInvoices = (invoices) => {
  return request({
    url: schoolAPI.SendInvoices(),
    method: 'POST',
    data: invoices,
  })
    .then((response) => response)
    .catch((error) => error);
};

// User Story 2: Invoice voiding
const VoidInvoice = (invoiceId, voidedBy) => {
  return request({
    url: schoolAPI.VoidInvoice(invoiceId),
    method: 'PUT',
    params: { voidedBy: voidedBy || 'System' },
  })
    .then((response) => response)
    .catch((error) => error);
};

// User Story 2: Get unpaid invoices
const GetUnpaidInvoices = (schoolId, matId) => {
  return request({
    url: schoolAPI.GetUnpaidInvoices(schoolId, matId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};

// User Story 2: Get subscription audit trail (AC 2.2)
const GetSubscriptionAuditTrail = (schoolId, matId, schoolSubscriptionId) => {
  return request({
    url: schoolAPI.GetSubscriptionAuditTrail(schoolId, matId, schoolSubscriptionId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const hasAdditionalSurvey = (schoolId) => {
  return request({
    url: schoolAPI.hasAdditionalSurvey(schoolId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const generateInvoiceForAdditionalSurveyBuy = (schoolId) => {
  return request({
    url: schoolAPI.generateInvoiceForAdditionalSurveyBuy(schoolId),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const GetAllSchoolsForSubscription = () => {
  return request({
    url: schoolAPI.GetAllSchoolsForSubscription(),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const UpdateSubscription = (data) => {
  return request({
    url: schoolAPI.UpdateSubscription(),
    method: 'POST',
    data: data,
  })
    .then((response) => response)
    .catch((error) => error);
};
const downLoadSchoolSubscription = (
  createdOnSort,
  StartDateSort,
  endDateSort,
  name,
  archivedOnly,
  testAccountOnly
) => {
  return request({
    url: schoolAPI.downLoadSchoolSubscription(
      createdOnSort,
      StartDateSort,
      endDateSort,
      name,
      archivedOnly,
      testAccountOnly
    ),
    method: 'GET',
    responseType: 'blob',
  })
    .then((response) => response)
    .catch((error) => error);
};
const getSentEmails = () => {
  return request({
    url: schoolAPI.getSentEmails(),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const GetSentEmailsWithDateTime = (val) => {
  return request({
    url: schoolAPI.GetSentEmailsWithDateTime(val),
    method: 'GET',
  })
    .then((response) => response)
    .catch((error) => error);
};
const downloadSESEmails = (name, from) => {
  return request({
    url: schoolAPI.downloadSESEmails(name, from),
    method: 'GET',
    responseType: 'blob',
  })
    .then((response) => response)
    .catch((error) => error);
};
const schoolService = {
  downloadSESEmails,
  getSentEmails,
  downLoadSchoolSubscription,
  UpdateSubscription,
  getSchoolList,
  getSchoolListPaginated,
  addSchool,
  addNewIndividualSchool,
  addNewIndividualSchoolPost,
  bulkAddSchools,
  checkSchoolDuplicate,
  getSchoolDetail,
  getSchool,
  updateSchoolDetail,
  updateSchoolDetailByHeadTeacher,
  updateSchoolAdminInfoByHeadTeacher,
  archiveSchool,
  unarchiveSchool,
  updateSchoolInvoiceInfo,
  getSchoolDetailByRegistrationToken,
  getAddressByPostCode,
  updateSchoolWelbeeVoice,
  getAddressSuggestions,
  getSchoolStaff,
  deleteSchoolStaff,
  updateSchoolStaff,
  getSchoolDetailBySuperAdmin,
  getNumberOfPupil,
  sendUpgradeInvoice,
  hideUpgradeBanner,
  getInfo,
  GetSchoolsForMat,
  GetSchoolsForMatWithCloseSurvey,
  getSchoolSubscriptions,
  addSchoolSubscriptions,
  GetOutstandingInvoices,
  SendInvoices,
  VoidInvoice, // User Story 2
  GetUnpaidInvoices, // User Story 2
  GetSubscriptionAuditTrail, // User Story 2: AC 2.2
  hasAdditionalSurvey,
  generateInvoiceForAdditionalSurveyBuy,
  GetAllSchoolsForSubscription,
  GetSentEmailsWithDateTime,
  updateSchoolDetailOnly,
  GetGIASSchools,
  GetSchoolForMATGUID,
  GetGIASSchoolsByURN,
  GetMatGroupDetailsFromGIAS,
  generateInvoiceForSubscription, // User Story 1.3: Generate invoice for existing subscription
};

export default schoolService;
